// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Per-Stargate detail page — status, remote controls, and device-scoped panels.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMqttActivity } from '../context/MqttActivityContext'; // Provider is now global in App.js
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import axiosBase from 'axios'; // Import base axios for CancelToken
import { fmtDateTime } from '../utils/timezoneUtils';
import { StargateHealthProvider } from '../context/StargateHealthContext';
import StargateCredentials from '../components/StargateCredentials';
import StargateHealthAwareWidgets from '../components/StargateHealthAwareWidgets';
import StargateSystemInfo from '../components/StargateSystemInfo';

import RDPViewer from '../components/RDPViewer';
import StargateUIViewer from '../components/StargateUIViewer';
import StargateDataMeter from '../components/StargateDataMeter';
import StargateEthernetClients from '../components/StargateEthernetClients';
import StargateVlanClients from '../components/StargateVlanClients';
import { createCancelableRemoteGet } from '../utils/remoteApiCancelable';
import { getHealthIndicatorClass } from '../utils/healthStatus';
import { stargateApiKeyEditable, stargateApiKeyText } from '../utils/stargateApiKey';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RouterIcon from '@mui/icons-material/Router';
import SettingsIcon from '@mui/icons-material/Settings';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import WifiIcon from '@mui/icons-material/Wifi';
import ComputerIcon from '@mui/icons-material/Computer';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useStargates } from '../context/StargatesContext';

const StargateDetailPage = () => {
  const { isDarkMode } = useTheme();
  // Hooks that provide values from context or routing
  const {
    activityMap = {},
    getStargateStatus,
    getStargateStatusByIp,
    serialToDeviceIdMap = {},
    setStargateList,
    deviceInfoMap = {},
  } = useMqttActivity() || {};
  const { stargateId } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { stargates: cachedStargates } = useStargates();

  // Use cached data from StargatesContext for instant render (no loading spinner)
  const cachedStargate = cachedStargates.find((sg) => sg.id === parseInt(stargateId, 10)) || null;

  // State declarations - initialize from cache if available
  const [stargate, setStargate] = useState(cachedStargate);
  const [loading, setLoading] = useState(!cachedStargate); // Skip loading if we have cached data
  const [error, setError] = useState('');
  const [showPythonClientModal, setShowPythonClientModal] = useState(false);
  const [lastSeenMqtt, setLastSeenMqtt] = useState(null);
  // API key edit state
  const [apiKeyEditOpen, setApiKeyEditOpen] = useState(false);
  const [apiKeyEditValue, setApiKeyEditValue] = useState('');
  const [apiKeyEditLoading, setApiKeyEditLoading] = useState(false);
  const [apiKeyEditError, setApiKeyEditError] = useState('');
  const [apiKeyEditSuccess, setApiKeyEditSuccess] = useState(false);
  // RDP viewer state
  const [rdpViewerOpen, setRdpViewerOpen] = useState(false);
  // UI viewer state
  const [uiViewerOpen, setUiViewerOpen] = useState(false);
  // Firmware version state
  const [firmwareVersion, setFirmwareVersion] = useState(null);
  const [firmwareLoading, setFirmwareLoading] = useState(false);
  // Remote UI version state
  const [remoteUiVersion, setRemoteUiVersion] = useState(null);
  const [remoteUiVersionLoading, setRemoteUiVersionLoading] = useState(false);
  // Uptime state
  const [uptime, setUptime] = useState(null);
  const [uptimeLoading, setUptimeLoading] = useState(false);
  // Cancel token for axios requests
  const cancelTokenSourceRef = useRef(null);
  const firmwareRequestRef = useRef(null);
  const uiVersionRequestRef = useRef(null);
  const uptimeRequestRef = useRef(null);

  // useEffect for lastSeenMqtt (depends on stargate, deviceInfoMap, activityMap)
  useEffect(() => {
    // Ensure stargate and deviceInfoMap are not null/undefined before accessing properties
    if (!stargate || !stargate.ip_address || !deviceInfoMap) {
      setLastSeenMqtt(null);
      return;
    }
    const deviceId = Object.keys(deviceInfoMap).find(
      (id) => deviceInfoMap[id]?.ip_address === stargate.ip_address
    );
    // Ensure activityMap is not null/undefined before accessing properties
    if (deviceId && activityMap && activityMap[deviceId]) {
      setLastSeenMqtt(activityMap[deviceId]);
    } else {
      setLastSeenMqtt(null);
    }
  }, [stargate, deviceInfoMap, activityMap]);

  // Trigger context collection for assistant - DISABLED (AI features turned off)
  const triggerContextCollection = useCallback(async (stargateId, cancelToken) => {
    // AI features are disabled, skip context collection
    return;
  }, []);

  // Ref to track whether we already have stargate data (avoids stale closure in useCallback)
  const stargateRef = useRef(stargate);
  stargateRef.current = stargate;

  // Fetch stargate details
  const fetchStargate = useCallback(async () => {
    // Only show loading if we don't have any cached data
    if (!stargateRef.current) {
      setLoading(true);
    }

    // Cancel any existing request
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Operation canceled due to new request');
    }

    // Create new cancel token using base axios - check if source method exists
    if (axiosBase.CancelToken && axiosBase.CancelToken.source) {
      cancelTokenSourceRef.current = axiosBase.CancelToken.source();
    } else {
      cancelTokenSourceRef.current = null;
    }

    try {
      const response = await axios.get(`/stargates/${stargateId}`, {
        cancelToken: cancelTokenSourceRef.current ? cancelTokenSourceRef.current.token : undefined,
      });
      setStargate(response.data);
      setError('');

      // Set global stargateList for MqttActivityContext correlation
      if (response.data && setStargateList) {
        setStargateList([response.data]);
      }

      // Trigger context collection for the assistant
      if (cancelTokenSourceRef.current) {
        await triggerContextCollection(stargateId, cancelTokenSourceRef.current.token);
      } else {
        await triggerContextCollection(stargateId, null);
      }
    } catch (err) {
      if (!axiosBase.isCancel || !axiosBase.isCancel(err)) {
        if (!stargateRef.current) {
          setError('Failed to load stargate details. Please try again later.');
        }
        console.error('Error fetching stargate:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [stargateId, setStargateList, triggerContextCollection]);

  // Fetch firmware version when stargate data is available
  const fetchFirmwareVersion = useCallback(async () => {
    if (!stargate?.ip_address || !stargate?.api_key) return;
    if (stargate?.health_status && stargate.health_status !== 'green') {
      setFirmwareVersion(
        stargate.health_status === 'yellow' || stargate.health_status === 'degraded'
          ? 'Device degraded'
          : 'Device offline'
      );
      return;
    }

    if (firmwareRequestRef.current?.cancel) {
      firmwareRequestRef.current.cancel();
    }

    setFirmwareLoading(true);
    try {
      const request = createCancelableRemoteGet(
        stargate.ip_address,
        stargate.api_key,
        '/api/firmware/version'
      );
      firmwareRequestRef.current = request;

      const versionData = await request.request;
      setFirmwareVersion(
        versionData?.current_version ||
          versionData?.version ||
          versionData?.firmware_version ||
          null
      );
    } catch (err) {
      if (axiosBase.isCancel(err)) return;
      console.warn('Failed to fetch firmware version:', err);
      setFirmwareVersion(null);
    } finally {
      setFirmwareLoading(false);
    }
  }, [stargate?.ip_address, stargate?.api_key, stargate?.health_status]);

  // Fetch remote UI version from Stargate device
  const fetchRemoteUiVersion = useCallback(async () => {
    if (!stargate?.ip_address || !stargate?.api_key) return;
    if (stargate?.health_status && stargate.health_status !== 'green') {
      setRemoteUiVersion(
        stargate.health_status === 'yellow' || stargate.health_status === 'degraded'
          ? 'Device degraded'
          : 'Device offline'
      );
      return;
    }

    if (uiVersionRequestRef.current?.cancel) {
      uiVersionRequestRef.current.cancel();
    }

    setRemoteUiVersionLoading(true);
    try {
      const request = createCancelableRemoteGet(
        stargate.ip_address,
        stargate.api_key,
        '/api/system/ui-version'
      );
      uiVersionRequestRef.current = request;

      const data = await request.request;
      setRemoteUiVersion(data?.version || 'N/A');
    } catch (err) {
      if (axiosBase.isCancel(err)) return;
      console.warn('Failed to fetch remote UI version:', err);
      setRemoteUiVersion('N/A');
    } finally {
      setRemoteUiVersionLoading(false);
    }
  }, [stargate?.ip_address, stargate?.api_key, stargate?.health_status]);

  // Fetch uptime when stargate data is available
  const fetchUptime = useCallback(async () => {
    if (!stargate?.ip_address || !stargate?.api_key) return;
    if (stargate?.health_status && stargate.health_status !== 'green') {
      setUptime(null);
      return;
    }

    if (uptimeRequestRef.current?.cancel) {
      uptimeRequestRef.current.cancel();
    }

    setUptimeLoading(true);
    try {
      const request = createCancelableRemoteGet(
        stargate.ip_address,
        stargate.api_key,
        '/api/system/status'
      );
      uptimeRequestRef.current = request;

      const statusData = await request.request;
      setUptime(statusData?.uptime || null);
    } catch (err) {
      if (axiosBase.isCancel(err)) return;
      console.warn('Failed to fetch uptime:', err);
      setUptime(null);
    } finally {
      setUptimeLoading(false);
    }
  }, [stargate?.ip_address, stargate?.api_key, stargate?.health_status]);

  // useEffect to fetch stargate on component mount
  useEffect(() => {
    fetchStargate();

    return () => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Component unmounted');
        cancelTokenSourceRef.current = null;
      }
    };
  }, [fetchStargate]);

  // Fetch firmware version, UI version, and uptime when stargate data changes
  useEffect(() => {
    if (stargate?.ip_address && stargate?.api_key) {
      fetchFirmwareVersion();
      fetchRemoteUiVersion();
      fetchUptime();
    }

    return () => {
      if (firmwareRequestRef.current?.cancel) {
        firmwareRequestRef.current.cancel();
        firmwareRequestRef.current = null;
      }
      if (uiVersionRequestRef.current?.cancel) {
        uiVersionRequestRef.current.cancel();
        uiVersionRequestRef.current = null;
      }
      if (uptimeRequestRef.current?.cancel) {
        uptimeRequestRef.current.cancel();
        uptimeRequestRef.current = null;
      }
    };
  }, [
    stargate?.ip_address,
    stargate?.api_key,
    fetchFirmwareVersion,
    fetchRemoteUiVersion,
    fetchUptime,
  ]);

  const renderHealthIndicator = (status) => {
    if (!status) {
      return (
        <span className="health-indicator health-gray">
          <i className="fas fa-circle-notch fa-spin"></i>
        </span>
      );
    }
    return <span className={`health-indicator ${getHealthIndicatorClass(status)}`}></span>;
  };

  // Render flag emoji
  const renderFlag = (countryCode) => {
    if (!countryCode) return null;

    // Check if countryCode is a string and has valid length
    if (typeof countryCode !== 'string' || countryCode.length !== 2) {
      console.warn('Invalid country code format:', countryCode);
      return null;
    }

    // Convert country code to flag emoji
    return countryCode
      .toUpperCase()
      .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
  };

  // Helper function to format uptime
  const formatUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stargate) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Stargate not found.
      </Alert>
    );
  }

  // --- API Key Edit Dialog ---
  const handleApiKeyEditOpen = () => {
    setApiKeyEditValue(stargate.api_key || '');
    setApiKeyEditError('');
    setApiKeyEditSuccess(false);
    setApiKeyEditOpen(true);
  };
  const handleApiKeyEditSave = async () => {
    setApiKeyEditLoading(true);
    setApiKeyEditError('');
    try {
      const response = await axios.put(`/stargates/${stargateId}`, {
        api_key: apiKeyEditValue,
      });
      // After updating, refetch from backend to ensure displayed API key matches backend
      await fetchStargate();
      setApiKeyEditSuccess(true);
      setTimeout(() => setApiKeyEditOpen(false), 1200);
    } catch (err) {
      setApiKeyEditError('Failed to update API key.');
    } finally {
      setApiKeyEditLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          Geographical Regions
        </Link>
        {stargate.region && (
          <Link
            to={`/regions/${stargate.region_id}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {stargate.region.name}{' '}
            {stargate.region.country_flag && renderFlag(stargate.region.country_flag)}
          </Link>
        )}
        <Typography color="text.primary">{stargate.friendly_name}</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            minWidth: 0,
          }}
        >
          <RouterIcon sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: 'text.primary',
              fontSize: { xs: '1.35rem', sm: undefined },
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {stargate.friendly_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {(() => {
              return renderHealthIndicator(stargate.health_status);
            })()}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
          }}
        >
          {stargate.ip_address && (
            <Button
              variant="outlined"
              onClick={() => window.open(`http://${stargate.ip_address}`, '_blank')}
              startIcon={<ComputerIcon />}
              color="primary"
            >
              View UI
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => navigate(`/regions/${stargate.region_id}`)}
            startIcon={<ArrowBackIcon />}
          >
            Back to Region
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Stargate Details and Credentials Side by Side */}
        <Grid item xs={12} md={6} sx={{ height: '100%' }}>
          <Card
            sx={{
              minHeight: 440,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardHeader title="Stargate Details" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Model
                  </Typography>
                  <Typography variant="body1">{stargate.model}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body1">{stargate.serial_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1">{stargate.ip_address || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  {/* Updated At: Last time this Stargate record was changed in the database */}
                  <Typography variant="subtitle2" color="text.secondary">
                    Updated At (Database)
                  </Typography>
                  <Typography variant="body1">
                    {stargate.updated_at ? fmtDateTime(stargate.updated_at) : 'Never'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Version
                  </Typography>
                  {/* Fixed-height value rows: the spinner<->text swap must not change row height */}
                  <Typography
                    variant="body1"
                    sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}
                  >
                    {firmwareLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      firmwareVersion || 'Unknown'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    UI Version
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}
                  >
                    {remoteUiVersionLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      remoteUiVersion || 'N/A'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Uptime
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}
                  >
                    {uptimeLoading ? <CircularProgress size={16} /> : formatUptime(uptime)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">{fmtDateTime(stargate.created_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  {/* Last Seen: Timestamp of the last MQTT message received from this Stargate */}
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Seen (MQTT)
                  </Typography>
                  <Typography variant="body1">
                    {stargate.last_seen ? fmtDateTime(stargate.last_seen) : 'Never'}
                  </Typography>
                </Grid>
                {/* API Key row - editable if admin */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    API Key
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                      {stargateApiKeyText(stargate)}
                    </Typography>
                    {stargateApiKeyEditable(stargate, isAdmin()) && (
                      <Tooltip title="Edit API Key">
                        <IconButton size="small" sx={{ ml: 1 }} onClick={handleApiKeyEditOpen}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} sx={{ height: '100%' }}>
          {/* Condensed Stargate Credentials */}
          <StargateCredentials
            stargateId={stargateId}
            condensed
            onShowPythonClient={() => setShowPythonClientModal(true)}
            sx={{
              minHeight: 440,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          />
        </Grid>
      </Grid>

      {/* Data Meter */}
      <Box sx={{ mt: 3 }}>
        <StargateDataMeter stargateId={stargateId} stargateName={stargate.friendly_name} />
      </Box>

      {/* Ethernet Clients */}
      {stargate.ip_address && stargate.api_key && (
        <Box sx={{ mt: 3 }}>
          <StargateEthernetClients
            ip={stargate.ip_address}
            apiKey={stargate.api_key}
            healthStatus={stargate.health_status}
          />
        </Box>
      )}

      {/* VLAN Tunnel Clients */}
      {stargate.ip_address && stargate.api_key && (
        <Box sx={{ mt: 3 }}>
          <StargateVlanClients
            ip={stargate.ip_address}
            apiKey={stargate.api_key}
            healthStatus={stargate.health_status}
          />
        </Box>
      )}

      {/* Stargate Command & Control Widgets with Health Check */}
      <Box sx={{ mt: 3 }}>
        {stargate.id ? (
          <StargateHealthProvider
            key={stargate.id}
            stargateId={stargate.id}
          >
            <StargateHealthAwareWidgets
              ip={stargate.ip_address}
              apiKey={stargate.api_key}
              stargate={stargate}
            />
          </StargateHealthProvider>
        ) : (
          <Card
            sx={{
              background: 'rgba(138, 148, 168, 0.06)',
              border: '1.5px solid',
              borderColor: 'divider',
              borderRadius: 3,
              boxShadow: 2,
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Stargate configuration incomplete. IP address or API key is missing.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Python MQTT Client Modal */}
      <Dialog
        open={showPythonClientModal}
        onClose={() => setShowPythonClientModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Python MQTT Client</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            Download a pre-configured Python MQTT client script for this Stargate. This script is
            ready to run and will automatically connect to the dashboard using the credentials
            above.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            {/* Download button will call download on the credentials component, so for now, instruct user to use the credentials panel or refactor to lift handler if needed. */}
            <StargateCredentials stargateId={stargateId} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            To use the script:
          </Typography>
          <Box component="ol" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">Transfer the script to your Stargate device</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Make it executable: <code>chmod +x stargate_mqtt_*.py</code>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Install dependencies: <code>pip install paho-mqtt</code>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Run the script: <code>./stargate_mqtt_*.py</code>
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPythonClientModal(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Edit Dialog */}
      <Dialog
        open={apiKeyEditOpen}
        onClose={() => setApiKeyEditOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="API Key"
            value={apiKeyEditValue}
            onChange={(e) => setApiKeyEditValue(e.target.value)}
            fullWidth
            autoFocus
            margin="normal"
            disabled={apiKeyEditLoading}
          />
          {apiKeyEditError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {apiKeyEditError}
            </Alert>
          )}
          {apiKeyEditSuccess && (
            <Alert severity="success" sx={{ mt: 1 }}>
              API Key updated!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyEditOpen(false)} disabled={apiKeyEditLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleApiKeyEditSave}
            disabled={apiKeyEditLoading || !apiKeyEditValue}
            variant="contained"
            color="primary"
          >
            {apiKeyEditLoading ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* RDP Viewer */}
      {stargate && (
        <RDPViewer
          deviceId={stargateId}
          hostname={stargate.ip_address}
          open={rdpViewerOpen}
          onClose={() => setRdpViewerOpen(false)}
        />
      )}

      {/* UI Viewer */}
      {stargate && (
        <StargateUIViewer
          hostname={stargate.ip_address}
          friendlyName={stargate.friendly_name}
          open={uiViewerOpen}
          onClose={() => setUiViewerOpen(false)}
        />
      )}
    </Box>
  );
};

export default StargateDetailPage;
